import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2, Check, AlertCircle, Globe, Key } from 'lucide-react';
import { AuthService } from '../../../../services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

interface EsentralSettingsState {
  'Esentral.Url': string;
  'Esentral.GoodsReceipt.Username': string;
  'Esentral.GoodsReceipt.Password': string;
  'Esentral.PickingList.Username': string;
  'Esentral.PickingList.Password': string;
  'Esentral.StockTransaction.Username': string;
  'Esentral.StockTransaction.Password': string;
}

type SettingKey = keyof EsentralSettingsState;

const SETTING_KEYS: SettingKey[] = [
  'Esentral.Url',
  'Esentral.GoodsReceipt.Username',
  'Esentral.GoodsReceipt.Password',
  'Esentral.PickingList.Username',
  'Esentral.PickingList.Password',
  'Esentral.StockTransaction.Username',
  'Esentral.StockTransaction.Password',
];

const DEFAULT_STATE: EsentralSettingsState = {
  'Esentral.Url': '',
  'Esentral.GoodsReceipt.Username': '',
  'Esentral.GoodsReceipt.Password': '',
  'Esentral.PickingList.Username': '',
  'Esentral.PickingList.Password': '',
  'Esentral.StockTransaction.Username': '',
  'Esentral.StockTransaction.Password': '',
};

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

export const EsentralSettings: React.FC = () => {
  const [settings, setSettings] = useState<EsentralSettingsState>(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  const fetchSetting = useCallback(
    async (key: SettingKey): Promise<string> => {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/settings/system/${encodeURIComponent(key)}`
      );
      if (response.status === 404) {
        return '';
      }
      if (!response.ok) {
        throw new Error(`Kunne ikke laste inn innstilling: ${key}`);
      }
      const data = await response.json();
      return data?.value ?? '';
    },
    []
  );

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const results = await Promise.all(SETTING_KEYS.map((key) => fetchSetting(key)));
        const loaded: EsentralSettingsState = { ...DEFAULT_STATE };
        SETTING_KEYS.forEach((key, index) => {
          loaded[key] = results[index];
        });
        setSettings(loaded);
      } catch (err) {
        setLoadError(err instanceof Error ? err.message : 'Ukjent feil ved innlasting');
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [fetchSetting]);

  const handleChange = (key: SettingKey, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const settingsDict: Record<string, string> = {};
      SETTING_KEYS.forEach((key) => { settingsDict[key] = settings[key]; });
      const body = { settings: settingsDict };
      const response = await AuthService.authenticatedPut(
        `${API_BASE_URL}/settings/system`,
        body
      );
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Ukjent feil ved lagring');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[var(--color-text-secondary)]">
        <Loader2 className="animate-spin mr-2 h-5 w-5" />
        <span>Laster inn innstillinger...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-red-700 dark:border-red-700 dark:bg-red-950 dark:text-red-300">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>{loadError}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
          eSENTRAL-integrasjon
        </h2>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          Konfigurasjon for dokumentmottak fra eSENTRAL. Innstillingene gjelder for denne
          leietakeren.
        </p>
      </div>

      {/* Tilkobling */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Globe className="h-4 w-4 text-[var(--color-text-secondary)]" />
          <h3 className="font-medium text-[var(--color-text-primary)]">Tilkobling</h3>
        </div>
        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          Base-URL til eSENTRAL API. Deles av alle dokumenttyper.
        </p>
        <div>
          <label
            htmlFor="esentral-url"
            className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]"
          >
            URL
          </label>
          <input
            id="esentral-url"
            type="url"
            value={settings['Esentral.Url']}
            onChange={(e) => handleChange('Esentral.Url', e.target.value)}
            placeholder="https://api.esentral.no/v1"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Varemottak */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Key className="h-4 w-4 text-[var(--color-text-secondary)]" />
          <h3 className="font-medium text-[var(--color-text-primary)]">Varemottak</h3>
        </div>
        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          Brukernavn og passord for mottak av varemottaksdokumenter fra eSENTRAL.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="goodsreceipt-username"
              className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              Brukernavn
            </label>
            <input
              id="goodsreceipt-username"
              type="text"
              value={settings['Esentral.GoodsReceipt.Username']}
              onChange={(e) => handleChange('Esentral.GoodsReceipt.Username', e.target.value)}
              autoComplete="off"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="goodsreceipt-password"
              className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              Passord
            </label>
            <input
              id="goodsreceipt-password"
              type="password"
              value={settings['Esentral.GoodsReceipt.Password']}
              onChange={(e) => handleChange('Esentral.GoodsReceipt.Password', e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Plukk */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Key className="h-4 w-4 text-[var(--color-text-secondary)]" />
          <h3 className="font-medium text-[var(--color-text-primary)]">Plukk</h3>
        </div>
        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          Brukernavn og passord for mottak av plukklister fra eSENTRAL.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="pickinglist-username"
              className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              Brukernavn
            </label>
            <input
              id="pickinglist-username"
              type="text"
              value={settings['Esentral.PickingList.Username']}
              onChange={(e) => handleChange('Esentral.PickingList.Username', e.target.value)}
              autoComplete="off"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="pickinglist-password"
              className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              Passord
            </label>
            <input
              id="pickinglist-password"
              type="password"
              value={settings['Esentral.PickingList.Password']}
              onChange={(e) => handleChange('Esentral.PickingList.Password', e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Lagertransaksjoner */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <Key className="h-4 w-4 text-[var(--color-text-secondary)]" />
          <h3 className="font-medium text-[var(--color-text-primary)]">
            Lagertransaksjoner (Telling / Korreksjon / Flytt)
          </h3>
        </div>
        <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
          Brukernavn og passord for mottak av lagertransaksjonsdokumenter fra eSENTRAL.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="stocktransaction-username"
              className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              Brukernavn
            </label>
            <input
              id="stocktransaction-username"
              type="text"
              value={settings['Esentral.StockTransaction.Username']}
              onChange={(e) => handleChange('Esentral.StockTransaction.Username', e.target.value)}
              autoComplete="off"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="stocktransaction-password"
              className="mb-1 block text-sm font-medium text-[var(--color-text-primary)]"
            >
              Passord
            </label>
            <input
              id="stocktransaction-password"
              type="password"
              value={settings['Esentral.StockTransaction.Password']}
              onChange={(e) => handleChange('Esentral.StockTransaction.Password', e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-secondary)] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Save button and feedback */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
        >
          {saveStatus === 'saving' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Lagrer...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Lagre
            </>
          )}
        </button>

        {saveStatus === 'success' && (
          <div className="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400">
            <Check className="h-4 w-4" />
            <span>Innstillingene ble lagret</span>
          </div>
        )}

        {saveStatus === 'error' && (
          <div className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{saveError ?? 'Lagring feilet'}</span>
          </div>
        )}
      </div>
    </div>
  );
};
